import { useCallback, useEffect, useRef, useState } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import {
  securePlayerSpot,
  getTournament,
  getPlayers,
  getPlayersLastUpdatedAt,
  updatePlayer,
  addPlayer,
  removePlayer,
  withdrawPlayer,
  refreshPlayerFromProfile,
  refreshAllPlayersFromProfiles,
  unblockPlayer,
  getBlockedPlayersCount,
  getConnectedPlayerProfile,
} from '@/lib/api';
import { getDirectorMessageThreads } from '@/lib/api/messages';
import { usePolledData } from '@/lib/usePolledData';
import {
  playerNameKey,
  getDuplicateKeys,
} from '@/components/players/player-utils';
import { useApiErrorHandler } from '@/lib/useApiErrorHandler';
import { showSuccessToast } from '@/lib/toast';
import { useTranslation } from 'react-i18next';
import type { Tournament, Player, PlayerStatus } from '@swiss-magic/shared-types';
import { getGameConfig } from '@swiss-magic/shared-types';
import { useInlineEdit } from './useInlineEdit';
import { useNewPlayerRow } from './useNewPlayerRow';
import { useNotesDialog } from './useNotesDialog';
import { useInviteDialog } from './useInviteDialog';
import { useRejectDialog } from './useRejectDialog';
import { useViewProfileDialog } from './useViewProfileDialog';

export function usePlayersController(
  tournamentId: string | undefined,
  navigate: NavigateFunction,
  // Story 083 — God Mode is owned hub-wide (the header toggle), passed
  // in via the outlet context. The controller no longer owns it.
  godMode: boolean,
) {
  const { t } = useTranslation('director');
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const prevPlayerCountRef = useRef(0);

  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const [viewMode, setViewMode] = useState<'default' | 'blocked'>('default');
  const [blockedCount, setBlockedCount] = useState(0);
  // The badge shows the active count even in the blocked view, so freeze the
  // last known active count whenever the default-view list reloads.
  const [activeCountSnapshot, setActiveCountSnapshot] = useState(0);

  // Story 050 — staleness signal. `playersSnapshotAt` records the
  // `MAX(updated_at)` returned alongside the most recent full list fetch
  // (or, on a brand-new tournament with no rows, the server's now() at
  // fetch time). The poller (below) bumps `staleSince` when the polled
  // value strictly succeeds the snapshot — that's what surfaces the
  // banner.
  const [playersSnapshotAt, setPlayersSnapshotAt] = useState<string | null>(null);
  const [staleSince, setStaleSince] = useState<string | null>(null);

  const handleError = useApiErrorHandler();

  // Story 050 follow-up — when a director's own action mutates the list,
  // bump the snapshot to the latest server timestamp so the next poll
  // doesn't surface the *director's own change* as a stale-list signal.
  // Used by every mutation path (sub-hooks + handlers below) instead of
  // raw setPlayers. The initial loadAll / switchView keep using setPlayers
  // directly because they manage the snapshot themselves from the same
  // response.
  //
  // BUT: if the banner is already up (an external change is pending),
  // bumping the snapshot would hide that pending change behind the
  // director's now-newer mutation. The polled signal is a global MAX, so
  // we can't tell "external change" and "own change" apart once both are
  // in. Skip the bump in that case and let banner-refresh (loadAll) be
  // the one path that legitimately clears staleSince. Read the latest
  // staleSince via ref to avoid re-creating commitPlayersChange on every
  // banner toggle (that would ripple through every sub-hook's deps).
  const staleSinceRef = useRef<string | null>(null);
  useEffect(() => {
    staleSinceRef.current = staleSince;
  }, [staleSince]);

  const commitPlayersChange: typeof setPlayers = useCallback(
    (updater) => {
      setPlayers(updater);
      if (!tournamentId) return;
      if (staleSinceRef.current != null) return;
      getPlayersLastUpdatedAt(tournamentId)
        .then(({ at }) => setPlayersSnapshotAt(at))
        .catch(() => {
          /* swallow — the next poll reconciles state */
        });
    },
    [tournamentId],
  );

  const inlineEdit = useInlineEdit({
    tournamentId,
    players,
    setPlayers: commitPlayersChange,
  });
  const newPlayerRow = useNewPlayerRow({
    tournamentId,
    players,
    setPlayers: commitPlayersChange,
    godMode,
    isChess: tournament?.game === 'chess',
  });
  const notesDialog = useNotesDialog({
    tournamentId,
    setPlayers: commitPlayersChange,
  });
  const inviteDialog = useInviteDialog({ tournamentId });
  const viewProfileDialog = useViewProfileDialog<Player>({
    fetchProfile: (player) => {
      if (!tournamentId) {
        return Promise.reject(new Error('No tournament'));
      }
      return getConnectedPlayerProfile(tournamentId, player.id);
    },
  });
  const rejectDialog = useRejectDialog({
    tournamentId,
    setPlayers: commitPlayersChange,
    setSelectedPlayer,
    onAfterReject: () => {
      void refreshBlockedCount();
    },
  });

  // Auto-scroll desktop table to bottom when a player is added, so the
  // always-present blank add-row stays in view even when the table is full.
  useEffect(() => {
    const prev = prevPlayerCountRef.current;
    prevPlayerCountRef.current = players.length;
    if (players.length > prev && tableScrollRef.current) {
      const el = tableScrollRef.current;
      requestAnimationFrame(() => {
        el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [players.length]);

  // Story 100 — secure a pending/waitlisted player's spot. Backend enforces
  // capacity for waitlisted promotions; its message passes through the toast.
  async function handleSecureSpot(player: Player) {
    if (!tournamentId) return;
    try {
      const updated = await securePlayerSpot(tournamentId, player.id);
      commitPlayersChange((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p)),
      );
    } catch (err) {
      handleError(err, 'Failed to secure the spot — try again');
    }
  }

  const loadAll = useCallback(async (): Promise<void> => {
    if (!tournamentId) return;
    const [t, { players: p, lastUpdatedAt }, b] = await Promise.all([
      getTournament(tournamentId),
      getPlayers(tournamentId, viewMode),
      getBlockedPlayersCount(tournamentId),
    ]);
    setTournament(t);
    setPlayers(p);
    setPlayersSnapshotAt(lastUpdatedAt);
    setStaleSince(null);
    if (viewMode === 'default') {
      const active = p.filter(
        (x) => x.status !== 'rejected' && x.status !== 'blocked' && x.status !== 'waitlisted',
      ).length;
      setActiveCountSnapshot(active);
    }
    setBlockedCount(b.count);
  }, [tournamentId, viewMode]);

  useEffect(() => {
    if (!tournamentId) return;
    loadAll()
      .catch(() => navigate('/dashboard'))
      .finally(() => setLoading(false));
    // The viewMode-triggered refetch lives in switchView; loadAll itself
    // is intentionally only invoked here on tournament-id changes (or via
    // refreshFromStaleBanner below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId, navigate]);

  // Story 050 — poll the lightweight last-updated-at signal. Compare
  // against the snapshot captured at the most recent full fetch; if the
  // server's max(updated_at) has moved past it, surface the banner.
  const { data: staleProbe } = usePolledData<{ at: string } | null>(
    () =>
      tournamentId
        ? getPlayersLastUpdatedAt(tournamentId)
        : Promise.resolve(null),
    [tournamentId],
  );

  useEffect(() => {
    const polledAt = staleProbe?.at;
    // Gate by tournament status — the banner only matters while
    // registration is open (when player profile updates and late
    // self-enrollments would otherwise silently desync the snapshot).
    // Once registration closes the roster is locked; surfacing or acting
    // on profile drift would mutate that locked roster on refresh.
    if (
      tournament?.status !== 'registration_open' ||
      !polledAt ||
      !playersSnapshotAt
    ) {
      setStaleSince(null);
      return;
    }
    setStaleSince(
      new Date(polledAt).getTime() > new Date(playersSnapshotAt).getTime()
        ? polledAt
        : null,
    );
  }, [staleProbe, playersSnapshotAt, tournament?.status]);

  async function refreshFromStaleBanner() {
    if (!tournamentId) return;
    try {
      // The banner can be triggered by either a player-row change OR a
      // connected player's profile change (the polled signal covers both
      // since story 050 follow-up). Bulk re-snapshot from profiles before
      // re-fetching, so the visible list reflects any profile edits the
      // banner may have surfaced.
      await refreshAllPlayersFromProfiles(tournamentId);
      await loadAll();
    } catch {
      // Same fallback as the initial load — bounce on hard error.
      navigate('/dashboard');
    }
  }

  async function refreshBlockedCount() {
    if (!tournamentId) return;
    try {
      const { count } = await getBlockedPlayersCount(tournamentId);
      setBlockedCount(count);
    } catch {
      /* count is non-critical; leave the previous value */
    }
  }

  async function switchView(mode: 'default' | 'blocked') {
    if (!tournamentId || mode === viewMode) return;
    try {
      const { players: list, lastUpdatedAt } = await getPlayers(
        tournamentId,
        mode,
      );
      setViewMode(mode);
      setPlayers(list);
      setPlayersSnapshotAt(lastUpdatedAt);
      setSelectedPlayer(null);
      if (mode === 'default') {
        const active = list.filter(
          (x) => x.status !== 'rejected' && x.status !== 'blocked' && x.status !== 'waitlisted',
        ).length;
        setActiveCountSnapshot(active);
      }
      await refreshBlockedCount();
    } catch (err) {
      handleError(err, t('players_page.toast.switch_view_error'));
    }
  }

  const gameConfig = getGameConfig(tournament?.game);
  const showRating = gameConfig.playerFields.rating;
  const showExternalId = gameConfig.playerFields.externalId;

  const isRegistrationOpen = tournament?.status === 'registration_open';
  const canEdit =
    tournament?.status === 'registration_open' ||
    tournament?.status === 'registration_closed';
  // Active = anyone who counts toward maxPlayers / the visible badge. Both
  // 'rejected' and 'blocked' rows are excluded — the badge has to keep showing
  // the active count even when the blocked-view filter is engaged.
  const activeCount =
    viewMode === 'blocked'
      ? activeCountSnapshot
      : players.filter(
          (p) => p.status !== 'rejected' && p.status !== 'blocked' && p.status !== 'waitlisted',
        ).length;
  const maxReached = tournament?.maxPlayers
    ? activeCount >= tournament.maxPlayers
    : false;
  const canAdd =
    viewMode === 'default' && (isRegistrationOpen || godMode) && !maxReached;

  const duplicateKeys = getDuplicateKeys(players);

  function isPlayerDuplicate(player: Player): boolean {
    const key = playerNameKey(player.firstName, player.lastName);
    return (
      duplicateKeys.has(key) ||
      (newPlayerRow.newRowMatchesExisting && key === newPlayerRow.newRowKey)
    );
  }

  async function handleAddPlayerMobile(data: {
    firstName: string;
    lastName: string;
    rating?: number;
    fideId?: number;
    city?: string;
    country?: string;
    club?: string;
  }) {
    if (!tournamentId) return;
    const player = await addPlayer(tournamentId, data, godMode);
    commitPlayersChange((prev) => [...prev, player]);
  }

  async function handleStatusChange(player: Player, status: PlayerStatus) {
    if (!tournamentId) return;
    // Rejection on a connected row goes through a dedicated dialog + endpoint
    // so the director can supply an optional reason and we can email the
    // player. Other transitions (accept / unset / etc.) keep the plain PATCH.
    if (status === 'rejected' && player.profileUserId) {
      rejectDialog.openRejectDialog(player);
      return;
    }
    const optimistic = { ...player, status };
    setPlayers((prev) => prev.map((p) => (p.id === player.id ? optimistic : p)));
    if (selectedPlayer?.id === player.id) setSelectedPlayer(optimistic);
    try {
      const updated = await updatePlayer(tournamentId, player.id, { status });
      commitPlayersChange((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      if (selectedPlayer?.id === updated.id) setSelectedPlayer(updated);
    } catch (err: unknown) {
      setPlayers((prev) => prev.map((p) => (p.id === player.id ? player : p)));
      if (selectedPlayer?.id === player.id) setSelectedPlayer(player);
      handleError(err, t('players_page.toast.status_error'));
    }
  }

  async function handleUpdatePlayer(player: Player, updates: Record<string, unknown>) {
    if (!tournamentId) return;
    const updated = await updatePlayer(tournamentId, player.id, updates);
    commitPlayersChange((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    if (selectedPlayer?.id === updated.id) setSelectedPlayer(updated);
  }

  async function handleRemove(playerId: string) {
    if (!tournamentId) return;
    await removePlayer(tournamentId, playerId);
    commitPlayersChange((prev) => prev.filter((p) => p.id !== playerId));
    // Removal of a connected row turns it into a blocked-and-archived entry —
    // bump the badge so the toolbar reflects reality without a reload.
    void refreshBlockedCount();
  }

  // Branching entry point for the row's Delete button. Confirmation only for
  // connected rows that aren't already blocked — that's the case where
  // Delete = block + archive, which the director needs to acknowledge.
  // Already-blocked rows archive immediately; unconnected rows hard-delete.
  const [blockConfirmTarget, setBlockConfirmTarget] = useState<Player | null>(
    null,
  );

  function requestRemovePlayer(player: Player) {
    if (player.profileUserId && player.status !== 'blocked') {
      setBlockConfirmTarget(player);
      return;
    }
    void handleRemove(player.id);
    if (selectedPlayer?.id === player.id) setSelectedPlayer(null);
  }

  function cancelBlockConfirm() {
    setBlockConfirmTarget(null);
  }

  async function confirmBlockAndRemove() {
    const target = blockConfirmTarget;
    if (!target) return;
    setBlockConfirmTarget(null);
    await handleRemove(target.id);
    if (selectedPlayer?.id === target.id) setSelectedPlayer(null);
  }

  const canWithdrawPlayers =
    godMode &&
    (tournament?.status === 'in_progress' ||
      tournament?.status === 'registration_closed');

  async function handleWithdraw(player: Player) {
    if (!tournamentId) return;
    try {
      const updated = await withdrawPlayer(tournamentId, player.id);
      commitPlayersChange((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      if (selectedPlayer?.id === updated.id) setSelectedPlayer(updated);
    } catch (err: unknown) {
      handleError(err, t('players_page.toast.withdraw_error'));
    }
  }

  async function handleUnblockPlayer(player: Player) {
    if (!tournamentId) return;
    try {
      const updated = await unblockPlayer(tournamentId, player.id);
      // In the blocked view we drop the unblocked row from the list; in the
      // default view we keep it (now status='rejected'). Either way the
      // toolbar's blocked count must drop by one.
      if (viewMode === 'blocked') {
        commitPlayersChange((prev) => prev.filter((p) => p.id !== updated.id));
      } else {
        commitPlayersChange((prev) =>
          prev.map((p) => (p.id === updated.id ? updated : p)),
        );
      }
      if (selectedPlayer?.id === updated.id) setSelectedPlayer(updated);
      showSuccessToast(t('players_page.toast.unblock_success'));
      void refreshBlockedCount();
    } catch (err: unknown) {
      handleError(err, t('players_page.toast.unblock_error'));
    }
  }

  // Jump into the director-side message thread with a reachable player:
  // reuse the existing thread when there is one, otherwise open the
  // composer pre-targeted at the player. Only players with a linked login
  // account (profileUserId) are reachable.
  async function messagePlayer(player: Player) {
    if (!tournamentId || !player.profileUserId) return;
    const recipientName =
      `${player.firstName} ${player.lastName}`.trim() || 'Player';
    try {
      const { threads } = await getDirectorMessageThreads(tournamentId);
      const existing = threads.find(
        (th) => th.playerUserId === player.profileUserId,
      );
      if (existing) {
        navigate(`/tournaments/${tournamentId}/messages/${existing.id}`);
        return;
      }
    } catch {
      // Fall through to the composer on a lookup failure.
    }
    navigate(
      `/tournaments/${tournamentId}/messages/new?playerUserId=${encodeURIComponent(
        player.profileUserId,
      )}`,
      { state: { recipientNames: { [player.profileUserId]: recipientName } } },
    );
  }

  async function handleRefreshFromProfile(player: Player) {
    if (!tournamentId) return;
    try {
      const updated = await refreshPlayerFromProfile(tournamentId, player.id);
      commitPlayersChange((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      if (selectedPlayer?.id === updated.id) setSelectedPlayer(updated);
      showSuccessToast(t('players_page.toast.refresh_success'));
    } catch (err: unknown) {
      handleError(err, t('players_page.toast.refresh_error'));
    }
  }

  const playerCountText = tournament?.maxPlayers
    ? t('players_page.count.with_max', {
        count: activeCount,
        max: tournament.maxPlayers,
      })
    : activeCount === 1
      ? t('players_page.count.singular')
      : t('players_page.count.plural', { count: activeCount });

  return {
    handleSecureSpot,
    tournament,
    setTournament,
    players,
    setPlayers,
    loading,
    ...newPlayerRow,
    ...notesDialog,
    ...inviteDialog,
    ...viewProfileDialog,
    ...rejectDialog,
    ...inlineEdit,
    selectedPlayer,
    setSelectedPlayer,
    showAddForm,
    setShowAddForm,
    godMode,
    tableScrollRef,
    isPlayerDuplicate,
    handleAddPlayerMobile,
    handleStatusChange,
    handleUpdatePlayer,
    handleRemove,
    handleWithdraw,
    handleRefreshFromProfile,
    handleUnblockPlayer,
    messagePlayer,
    requestRemovePlayer,
    blockConfirmTarget,
    cancelBlockConfirm,
    confirmBlockAndRemove,
    showRating,
    showExternalId,
    isRegistrationOpen,
    canEdit,
    activeCount,
    maxReached,
    canAdd,
    duplicateKeys,
    canWithdrawPlayers,
    playerCountText,
    gameConfig,
    viewMode,
    blockedCount,
    switchView,
    staleSince,
    refreshFromStaleBanner,
    // Exposed so sub-pages can refresh after a hub-lifecycle action
    // (e.g. closing registration) mutates the tournament server-side
    // outside this controller's own action paths.
    reload: loadAll,
  };
}

export type PlayersController = ReturnType<typeof usePlayersController>;
